// src/components/admin/AdminSidebar.js
import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Box,
  Avatar,
  Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LayersIcon from "@mui/icons-material/Layers";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation } from "react-router-dom";
import { themeColors } from "../../theme/colors";
import logo from "../../Assets/logo.png";

const drawerWidth = 240;
const collapsedWidth = 60;

const BrokerSidebar = ({ mobileOpen, handleDrawerToggle, desktopOpen, handleDesktopToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    // توجه إلى صفحة تسجيل الدخول عبر تغيير href — هذا يضمن إعادة تحميل المتصفح (ريفريش)
    window.location.href = "/login";
  };

  const menuItems = [
    {
      text: "الرئيسية",
      icon: <DashboardIcon />,
      path: "/broker-dashboard",
    },
    {
      text: "جميع النسخ",
      icon: <LayersIcon />,
      path: "/broker-all-versions",
    },
    {
      text: "طلبات النسخ الجديدة",
      icon: <AssignmentIcon />,
      path: "/broker-new-requests",
    },
    {
      text: "العملاء",
      icon: <PeopleIcon />,
      path: "/broker-customer-accounts",
    },
    {
      text: "الباقات",
      icon: <CreditCardIcon />,
      path: "/broker-planes",
    },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: themeColors.background.sidebar,
        color: themeColors.text.white,
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: desktopOpen ? "20px 16px" : "20px 8px",
          minHeight: "80px",
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
        }}
      >
        {desktopOpen ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logo}
              alt="RestoCash Logo"
              sx={{
                width: 45,
                height: 45,
                backgroundColor: themeColors.background.paper,
                padding: "4px",
              }}
            />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: themeColors.primary.main,
                  lineHeight: 1.2,
                }}
              >
                RestoCash
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: themeColors.text.light,
                  fontSize: "0.7rem",
                }}
              >
                نظام إدارة المطاعم
              </Typography>
            </Box>
          </Box>
        ) : (
          <Avatar
            src={logo}
            alt="RestoCash"
            sx={{
              width: 40,
              height: 40,
              backgroundColor: themeColors.background.paper,
              padding: "4px",
            }}
          />
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ flex: 1, px: 1, pt: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip
              key={item.path}
              title={!desktopOpen ? item.text : ""}
              placement="left"
              arrow
            >
              <ListItem
                button
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  backgroundColor: isActive
                    ? themeColors.primary.main
                    : "transparent",
                  color: isActive
                    ? themeColors.text.white
                    : themeColors.text.light,
                  justifyContent: desktopOpen ? "flex-start" : "center",
                  px: desktopOpen ? 2 : 1.5,
                  py: 1.5,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: isActive
                      ? themeColors.primary.dark
                      : "rgba(255,255,255,0.08)",
                    color: themeColors.text.white,
                    transform: "translateX(-4px)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: desktopOpen ? 40 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {desktopOpen && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.95rem",
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 1 }} />

      {/* Logout Button */}
      <List sx={{ px: 1, pb: 2 }}>
        <Tooltip title={!desktopOpen ? "تسجيل الخروج" : ""} placement="left" arrow>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: themeColors.text.light,
              justifyContent: desktopOpen ? "flex-start" : "center",
              px: desktopOpen ? 2 : 1.5,
              py: 1.5,
              "&:hover": {
                backgroundColor: "rgba(220, 53, 69, 0.15)",
                color: themeColors.error,
                transform: "translateX(-4px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: desktopOpen ? 40 : "auto",
                justifyContent: "center",
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {desktopOpen && (
              <ListItemText
                primary="تسجيل الخروج"
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );

  return (
    <nav>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxShadow: "0 0 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: desktopOpen ? drawerWidth : collapsedWidth,
            overflowX: "hidden",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "-4px 0 12px rgba(0,0,0,0.08)",
            border: "none",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </nav>
  );
};

export default BrokerSidebar;
